/**
 * The fourth tab (P2-TR3, NFR-1).
 *
 * Four destinations fit a thumb; the farm has more than four screens. This is
 * where the rest live — the daily jobs first, because they are done every day,
 * and the reference screens after.
 */

import { Link } from 'react-router-dom';

import { useTranslation } from '../../i18n/useTranslation.js';
import { useAuth } from '../auth/AuthContext.js';

const GROUPS = [
  {
    titleKey: 'more.daily',
    links: [
      { to: '/operations/daily-log', key: 'daily.title' },
      { to: '/operations/milk', key: 'milk.title' },
      { to: '/operations/inventory', key: 'inventory.title' },
      { to: '/operations/milk-round', key: 'milk.delivery.title' },
    ],
  },
  {
    titleKey: 'more.money',
    links: [
      { to: '/reports', key: 'reports.title' },
      { to: '/receivables', key: 'receivables.title' },
      { to: '/reconcile', key: 'reconcile.title' },
      { to: '/customers', key: 'customers.title' },
    ],
  },
  {
    titleKey: 'more.records',
    links: [
      { to: '/books', key: 'finance.books.title' },
      { to: '/partners', key: 'finance.partners.title' },
      { to: '/vendors', key: 'finance.vendors.title' },
      { to: '/assets', key: 'finance.assets.title' },
      { to: '/setup-costs', key: 'setup.title' },
    ],
  },
  {
    titleKey: 'more.governance',
    links: [
      { to: '/meetings', key: 'meetings.title' },
      { to: '/tasks', key: 'tasks.title' },
      { to: '/audit', key: 'audit.title' },
      { to: '/qurbani', key: 'qurbani.title' },
      { to: '/cameras', key: 'cctv.title' },
      { to: '/users', key: 'users.title' },
    ],
  },
] as const;

export function MorePage(): JSX.Element {
  const { t } = useTranslation();
  const { user, logout } = useAuth();

  return (
    <div className="page-container">
      <h1 className="py-4 text-xl font-semibold text-gray-800">{t('nav.more')}</h1>

      {GROUPS.map((group) => (
        <section key={group.titleKey} className="mb-5">
          <h2 className="mb-2 text-sm font-medium text-gray-500">{t(group.titleKey)}</h2>
          <ul className="space-y-2">
            {group.links.map((link) => (
              <li key={link.to}>
                <Link
                  to={link.to}
                  className="flex min-h-touch items-center justify-between rounded-xl bg-white px-4 py-3 text-gray-800 shadow-sm"
                >
                  <span>{t(link.key)}</span>
                </Link>
              </li>
            ))}
          </ul>
        </section>
      ))}

      <div className="mt-8 rounded-xl bg-white px-4 py-3 shadow-sm">
        <p className="text-sm text-gray-800">{user?.fullName}</p>
        <p className="text-xs text-gray-500">{user ? t(`role.${user.role}`) : ''}</p>
      </div>

      <button
        type="button"
        onClick={() => void logout()}
        className="mt-3 min-h-touch w-full rounded-xl bg-white px-4 py-3 text-red-700 shadow-sm"
      >
        {t('auth.logout')}
      </button>
    </div>
  );
}
